<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisField from 'dashboard/components-next/LaisField.vue';

const props = defineProps({
  errors: { type: Object, default: () => ({}) },
  installment: { type: Object, required: true },
  payerOptions: { type: Array, default: () => [] },
});

const emit = defineEmits(['updatePayer', 'updatePayerCount']);
const { t } = useI18n();
const hasPayerOptions = computed(() => props.payerOptions.length > 0);
const payerCountOptions = computed(() =>
  Array.from({ length: props.payerOptions.length }, (_, index) => index + 1)
);
const optionLabel = option => option.label || option.name || option.nome;
</script>

<template>
  <div class="mt-4 rounded-lais border border-lais-border-soft bg-white p-3">
    <div class="mb-3 flex items-end justify-between gap-3">
      <h4 class="text-sm font-semibold text-lais-text">
        {{ t('SALES.COMMISSION.PAYMENT.PAYERS') }}
      </h4>
      <LaisField
        class="w-32 shrink-0"
        :label="t('SALES.COMMISSION.FIELDS.PAYER_COUNT')"
      >
        <select
          :value="hasPayerOptions ? installment.payers?.length || 1 : ''"
          :disabled="!hasPayerOptions"
          data-test="commission-payer-count"
          class="min-h-9 w-full rounded-lais border border-lais-border px-2 text-sm disabled:cursor-not-allowed disabled:bg-lais-bg-soft"
          @change="emit('updatePayerCount', $event.target.value)"
        >
          <option value="">{{ t('SALES.COMMISSION.COMMON.SELECT') }}</option>
          <option
            v-for="count in payerCountOptions"
            :key="count"
            :value="count"
          >
            {{ count }}
          </option>
        </select>
      </LaisField>
    </div>
    <div
      v-if="!hasPayerOptions"
      class="mb-3 flex items-start gap-2 rounded-lais border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
      data-test="commission-payer-empty-alert"
    >
      <span
        class="i-lucide-circle-alert mt-0.5 size-4 shrink-0 text-amber-600"
      />
      <span>{{ t('SALES.COMMISSION.PAYMENT.PAYERS_MISSING') }}</span>
    </div>
    <div class="grid gap-3 md:grid-cols-2">
      <div
        v-for="(payer, payerIndex) in installment.payers"
        :key="payerIndex"
        class="grid gap-2 md:grid-cols-2"
      >
        <LaisField :label="t('SALES.COMMISSION.FIELDS.PAYER')" required>
          <select
            :value="payer.user_id"
            :disabled="!hasPayerOptions"
            class="min-h-10 w-full rounded-lais border border-lais-border bg-white px-3 text-sm disabled:cursor-not-allowed disabled:bg-lais-bg-soft"
            @change="
              emit('updatePayer', payerIndex, 'user_id', $event.target.value)
            "
          >
            <option value="">{{ t('SALES.COMMISSION.COMMON.SELECT') }}</option>
            <option
              v-for="option in payerOptions"
              :key="option.id"
              :value="option.id"
            >
              {{ optionLabel(option) }}
            </option>
          </select>
        </LaisField>
        <LaisField :label="t('SALES.COMMISSION.FIELDS.PAYER_AMOUNT')" required>
          <input
            :value="payer.amount"
            :disabled="!hasPayerOptions"
            inputmode="decimal"
            class="min-h-10 w-full rounded-lais border border-lais-border bg-white px-3 text-sm disabled:cursor-not-allowed disabled:bg-lais-bg-soft"
            :placeholder="t('SALES.COMMISSION.PLACEHOLDERS.MONEY')"
            @input="
              emit('updatePayer', payerIndex, 'amount', $event.target.value)
            "
          />
        </LaisField>
      </div>
    </div>
    <p v-if="errors.payers" class="mt-2 text-sm text-lais-danger">
      {{ errors.payers }}
    </p>
  </div>
</template>
